/**
 * ===========================================
 * StudySpace ERP Frontend - Unauthorized Page
 * ===========================================
 * 
 * Access denied page displayed when users try to access
 * resources they don't have permission for.
 * 
 * Features:
 * - Clear access denied message
 * - Explanation of lack of permissions
 * - Button to redirect to login or dashboard
 * - Professional, non-alarming design
 * 
 * @file src/pages/auth/UnauthorizedPage.jsx
 * @description Unauthorized access page
 * @task T102
 */

import { Link, useNavigate } from 'react-router-dom';
import { ShieldX, LogIn, Home, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

/**
 * UnauthorizedPage Component
 * 
 * Displays an access denied message when users try to access
 * pages they don't have permission to view. Provides clear
 * navigation options based on authentication status.
 * 
 * @returns {JSX.Element} Unauthorized page component
 */
const UnauthorizedPage = () => {
  // Get auth state to determine navigation options
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon Container */}
        <div className="flex justify-center mb-6">
          <div className="h-24 w-24 bg-red-100 rounded-full flex items-center justify-center">
            <ShieldX className="h-14 w-14 text-red-500" />
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Access Denied
        </h1>

        {/* Explanation Message */}
        <p className="text-lg text-gray-600 mb-6">
          Sorry, you don't have permission to access this page.
        </p>

        {/* Additional Context */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Why am I seeing this?
          </h2>
          <ul className="text-left text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">•</span>
              <span>
                This page requires specific permissions that your account doesn't have.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">•</span>
              <span>
                You may need to log in with a different account.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">•</span>
              <span>
                If you believe you should have access, please contact an administrator.
              </span>
            </li>
          </ul>

          {isAuthenticated && user && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Currently logged in as:{' '}
                <span className="font-medium text-gray-700">{user.email}</span>
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {/* Go Back Button */}
          <button
            onClick={handleGoBack}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 
                     bg-gray-100 text-gray-700 font-medium rounded-lg
                     hover:bg-gray-200 transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Go Back</span>
          </button>

          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-6 py-3
                       bg-primary-600 text-white font-medium rounded-lg
                       hover:bg-primary-700 transition-colors duration-200"
            >
              <Home className="h-5 w-5" />
              <span>Go to Dashboard</span>
            </Link>
          ) : (
            /* Login Button - For unauthenticated users */
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3
                       bg-primary-600 text-white font-medium rounded-lg
                       hover:bg-primary-700 transition-colors duration-200"
            >
              <LogIn className="h-5 w-5" />
              <span>Sign In</span>
            </Link>
          )}
        </div>

        {/* Help Link */}
        <p className="mt-8 text-sm text-gray-500">
          Need help?{' '}
          <a
            href="mailto:support@studyspace.com"
            className="text-primary-600 hover:text-primary-700 hover:underline"
          >
            Contact Support
          </a>
        </p>

        {/* Error Code (for debugging) */}
        <p className="mt-4 text-xs text-gray-400">
          Error Code: 403 - Forbidden
        </p>
      </div>
    </div>
  );
};

export default UnauthorizedPage;
